/** Shared types and formatting for the staff-side student dossier. */

export interface StudentSection {
  key: string;
  /** Coarse bucket: Identidade, Academia, Saúde, Financeiro. */
  group: string;
  label: string;
  endpoint: string;
  status: number;
  data: unknown;
  error: string | null;
}

export interface StudentDossier {
  id: number;
  sections: StudentSection[];
}

/** Field names that deserve a friendlier label than the raw key. */
export const FIELD_LABELS: Record<string, string> = {
  idCliente: "ID do aluno",
  idProspect: "ID do prospect",
  idFilial: "Filial",
  nomeFilial: "Filial",
  nome: "Nome",
  snome: "Sobrenome",
  nomeCompleto: "Nome completo",
  nomeRegistro: "Nome (registro)",
  snomeRegistro: "Sobrenome (registro)",
  flNomePreferencial: "Usa nome preferencial",
  cpf: "CPF",
  passaporte: "Passaporte",
  dtNascimento: "Nascimento",
  dataAniversario: "Nascimento",
  idade: "Idade",
  sexo: "Sexo",
  sexoBiologico: "Sexo biológico",
  email: "E-mail",
  celular: "Celular",
  telefone: "Telefone",
  cep: "CEP",
  endereco: "Endereço",
  numero: "Número",
  complemento: "Complemento",
  bairro: "Bairro",
  cidade: "Cidade",
  idEstado: "Estado (id)",
  dataUltimoAcesso: "Último acesso",
  ultimoAcesso: "Último acesso",
  nivel: "Nível",
  peso: "Peso",
  altura: "Altura",
  morfologia: "Morfologia",
  objetivo: "Objetivo",
  objetivoDs: "Objetivo (descrição)",
  qtdeSessoes: "Sessões usadas",
  qtdeReposicoes: "Reposições",
  qtdeLimiteSemanal: "Limite semanal",
  qtdeLimiteMensal: "Limite mensal",
  flIlimitado: "Ilimitado",
  nomesContratos: "Contratos",
  sessoesDisponiveis: "Sessões disponíveis",
  quantidadeMaximaChekinPorDia: "Check-ins por dia",
  flNecessitaDeAprovacao: "Precisa de aprovação",
  flCadastroAprovado: "Cadastro aprovado",
  flRestrito: "Restrito",
  flSuspenso: "Suspenso",
  flInativo: "Inativo",
  flPersonalTrainer: "Personal trainer",
  possuiBiometria: "Possui biometria",
  fitCoins: "FitCoins",
  saldoDevedor: "Saldo devedor",
  saldoCredor: "Saldo credor",
  dsRecebimento: "Recebimento",
  dtVencimento: "Vencimento",
  dtLancamento: "Lançamento",
  valor: "Valor",
  descricao: "Descrição",
  tipoContato: "Tipo de contato",
  idTelefone: "ID do telefone",
};

export function humanize(key: string): string {
  const mapped = FIELD_LABELS[key];
  if (mapped) return mapped;
  const spaced = key
    .replace(/^fl(?=[A-Z])/, "")
    .replace(/^id(?=[A-Z])/, "id ")
    .replace(/^dt(?=[A-Z])/, "data ")
    .replace(/^qtde?(?=[A-Z])/, "quantidade ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Reads a printable string out of an unknown JSON value. */
export function asText(value: unknown): string | null {
  if (typeof value === "string") return value.trim() === "" ? null : value;
  if (typeof value === "number") return String(value);
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const described = (value as Record<string, unknown>).descricao;
    if (typeof described === "string" && described.trim() !== "") return described;
  }
  return null;
}

export function asNumber(value: unknown): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string") {
    const parsed = Number(value.replace(",", "."));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

export function asBool(value: unknown): boolean | null {
  return typeof value === "boolean" ? value : null;
}

export function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

/** Formats a scalar for display; ISO dates become pt-BR, CPF gets masked. */
export function formatValue(key: string, value: unknown): string | null {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "boolean") return value ? "sim" : "não";
  if (typeof value === "number") return new Intl.NumberFormat("pt-BR").format(value);
  const text = asText(value) ?? String(value);
  if (key.toLowerCase() === "cpf" && text.length >= 4) return `***.***.***-${text.slice(-2)}`;
  if (/^\d{4}-\d{2}-\d{2}T/.test(text)) return new Date(text).toLocaleString("pt-BR");
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return new Date(`${text}T12:00:00`).toLocaleDateString("pt-BR");
  return text;
}

/** Section lookup by key. */
export function sectionOf(dossier: StudentDossier | null, key: string): StudentSection | null {
  return dossier?.sections.find((s) => s.key === key) ?? null;
}

/** Field lookup inside a section, tolerating null/absent data. */
export function fieldOf(dossier: StudentDossier | null, key: string, field: string): unknown {
  const data = sectionOf(dossier, key)?.data;
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;
  return (data as Record<string, unknown>)[field] ?? null;
}
